import * as THREE from 'three';
import { MATERIALS } from '../data/materials.js';
import { OBJECT_BY_ID } from '../data/objectCatalog.js';
import { createHouseArchitecture, getHouseFloorMassing } from './generators/HouseDesignGenerator.js';

const FLOOR_HEIGHT = 3.2;
const WALL_HEIGHT = 3;
const WALL_THICKNESS = 0.16;

function clone(value) {
  return structuredClone(value);
}

function objectId(templateId, floorLevel, type, index) {
  return `${templateId}:${floorLevel}:${type}:${index}`;
}

function makeFurniture(template, floor, catalogId, index, x, z, rotation = 0, overrides = {}) {
  const catalog = OBJECT_BY_ID.get(catalogId);
  return {
    id: objectId(template.id, floor.level, catalogId, index),
    catalogId,
    name: overrides.name ?? catalog?.name ?? 'Object',
    type: overrides.type ?? 'furniture',
    category: catalog?.category ?? 'decoration',
    subcategory: catalog?.generatorType ?? 'object',
    generatorType: overrides.generatorType ?? catalog?.generatorType,
    position: [x, (floor.baseY ?? floor.level * FLOOR_HEIGHT) + 0.03, z],
    rotation: [0, rotation, 0],
    scale: [1, 1, 1],
    dimensions: clone(overrides.dimensions ?? catalog?.dimensions ?? [1, 1, 1]),
    material: overrides.material ?? catalog?.defaultMaterial ?? 'floor-oak',
    materialOverrides: {},
    floorId: floor.id,
    roomId: floor.rooms[0].id,
    locked: false,
    visible: true,
    placementRules: { snapToFloor: true }
  };
}

function addFurniturePack(template, floor, width, depth, centerX = 0, centerZ = 0) {
  const objects = [];
  let index = 0;
  const add = (catalogId, x, z, rotation = 0, overrides = {}) => objects.push(makeFurniture(
    template,
    floor,
    catalogId,
    index++,
    centerX + x,
    centerZ + z,
    rotation,
    overrides
  ));
  const type = template.generatorType;

  if (type === 'house' || type === 'villa') {
    add('sofa-basic', -width * 0.2, depth * 0.12, Math.PI);
    add('table-dining', width * 0.19, depth * 0.13);
    add('chair-simple', width * 0.19, depth * 0.25, Math.PI);
    add('chair-simple', width * 0.19, depth * 0.01, 0);
    add('bed-double', -width * 0.2, -depth * 0.25);
    add('cabinet-basic', width * 0.35, -depth * 0.34, Math.PI);
    add('counter-service', width * 0.2, -depth * 0.28, Math.PI / 2, { name: 'Kitchen Counter' });
  } else if (type === 'cafe' || type === 'food') {
    add('counter-service', 0, -depth * 0.32);
    add('retail-shelf', -width * 0.38, -depth * 0.32);
    add('menu-board', 0, -depth * 0.49, 0, { positionMode: 'wall' });
    for (const x of [-width * 0.25, width * 0.25]) {
      for (const z of [-depth * 0.05, depth * 0.25]) {
        add('table-dining', x, z, 0, { dimensions: [1.25, 0.78, 0.75] });
        add('chair-simple', x, z - 0.65, 0);
        add('chair-simple', x, z + 0.65, Math.PI);
      }
    }
  } else if (type === 'laundry') {
    add('counter-service', width * 0.2, -depth * 0.32);
    for (let x = -width * 0.35; x <= width * 0.1; x += 1.15) add('washer-front', x, -depth * 0.33);
    add('table-dining', 0, depth * 0.08, 0, { name: 'Folding Table', dimensions: [2.1, 0.88, 0.9] });
    add('retail-shelf', width * 0.38, -depth * 0.25);
    add('chair-simple', -width * 0.28, depth * 0.3, Math.PI);
    add('chair-simple', -width * 0.16, depth * 0.3, Math.PI);
  } else if (type === 'barbershop') {
    add('counter-service', width * 0.3, -depth * 0.32, 0, { name: 'Cashier Counter' });
    for (const x of [-width * 0.28, 0, width * 0.28]) {
      add('barber-chair', x, -depth * 0.04, Math.PI);
      add('menu-board', x, -depth * 0.48, 0, { name: 'Mirror', material: 'glass-clear', dimensions: [1.1, 1.5, 0.06] });
    }
    add('chair-simple', -width * 0.26, depth * 0.32, Math.PI);
    add('chair-simple', -width * 0.13, depth * 0.32, Math.PI);
  } else if (type === 'office') {
    add('reception-desk', 0, depth * 0.3, Math.PI);
    for (const x of [-width * 0.25, width * 0.25]) {
      add('office-desk', x, 0);
      add('chair-simple', x, 0.65, Math.PI);
    }
    add('meeting-table', 0, -depth * 0.28);
    add('chair-simple', -1.1, -depth * 0.28, -Math.PI / 2);
    add('chair-simple', 1.1, -depth * 0.28, Math.PI / 2);
    add('cabinet-basic', width * 0.4, -depth * 0.36);
  } else if (type === 'retail') {
    add('counter-service', width * 0.28, depth * 0.3, Math.PI);
    for (const x of [-width * 0.28, 0, width * 0.28]) add('retail-shelf', x, -depth * 0.12);
    add('retail-shelf', -width * 0.4, -depth * 0.34, Math.PI / 2);
    add('menu-board', 0, -depth * 0.48);
  } else if (type === 'education') {
    add('menu-board', 0, -depth * 0.48, 0, { name: 'Teaching Board', dimensions: [2.4, 1.3, 0.07] });
    for (const z of [-depth * 0.18, depth * 0.06, depth * 0.28]) {
      for (const x of [-width * 0.26, 0, width * 0.26]) {
        add('office-desk', x, z, 0, { dimensions: [1.05, 0.72, 0.55] });
        add('chair-simple', x, z + 0.48, Math.PI);
      }
    }
  } else if (type === 'hospitality') {
    add('reception-desk', 0, depth * 0.32, Math.PI);
    add('sofa-basic', -width * 0.25, depth * 0.05, Math.PI / 2);
    add('table-dining', 0, depth * 0.04, 0, { dimensions: [1.1, 0.55, 0.75] });
    add('bed-double', width * 0.2, -depth * 0.24);
    add('cabinet-basic', -width * 0.36, -depth * 0.32);
  } else if (type === 'religious') {
    add('retail-shelf', -width * 0.4, depth * 0.32, Math.PI / 2, { name: 'Shoe / Item Shelf' });
    if (template.categoryId === 'church') {
      for (const z of [-depth * 0.12, depth * 0.12, depth * 0.32]) {
        for (const x of [-width * 0.2, width * 0.2]) add('chair-simple', x, z, Math.PI);
      }
    }
    add('table-dining', 0, -depth * 0.34, 0, { name: 'Neutral Front Table', dimensions: [2, 0.78, 0.72] });
  } else if (type === 'studio') {
    add('office-desk', width * 0.22, -depth * 0.3);
    add('chair-simple', width * 0.22, -depth * 0.18, Math.PI);
    add('menu-board', 0, -depth * 0.46, 0, { name: 'Backdrop', dimensions: [3, 2.2, 0.08], material: 'wall-sage' });
    add('pendant-light', -width * 0.25, 0);
    add('pendant-light', width * 0.25, 0);
    add('sofa-basic', -width * 0.25, depth * 0.28, Math.PI);
  } else if (type === 'service') {
    add('reception-desk', 0, depth * 0.28, Math.PI);
    add('cabinet-basic', width * 0.36, -depth * 0.32);
    add('office-desk', 0, -depth * 0.16);
    add('chair-simple', 0, -depth * 0.04, Math.PI);
    add('chair-simple', -width * 0.24, depth * 0.25, Math.PI);
    add('chair-simple', -width * 0.12, depth * 0.25, Math.PI);
  } else {
    add('reception-desk', 0, depth * 0.3, Math.PI);
    add('table-dining', 0, 0);
    add('chair-simple', -0.8, 0, -Math.PI / 2);
    add('chair-simple', 0.8, 0, Math.PI / 2);
    add('retail-shelf', width * 0.37, -depth * 0.3);
  }

  add('plant-pot', -width * 0.4, -depth * 0.36);
  return objects;
}

function makeFloor(template, level) {
  const massing = template.houseProfile
    ? getHouseFloorMassing(template, level)
    : {
        width: template.footprint.width - level * 0.35,
        depth: template.footprint.depth - level * 0.25,
        centerX: 0,
        centerZ: 0,
        baseY: level * FLOOR_HEIGHT
      };
  const { width, depth, centerX, centerZ, baseY } = massing;
  const left = centerX - width / 2;
  const right = centerX + width / 2;
  const front = centerZ + depth / 2;
  const back = centerZ - depth / 2;
  const id = objectId(template.id, level, 'floor-record', 0);
  const roomId = objectId(template.id, level, 'room', 0);
  const wallMaterial = template.houseProfile
    ? (level === 0 ? template.houseProfile.wall : template.houseProfile.upperWall)
    : (level === 0 ? 'wall-warm' : 'wall-sky');
  const walls = [];
  const wall = (index, start, end) => ({
    id: objectId(template.id, level, 'wall', index),
    name: `Wall ${index + 1}`,
    type: 'wall',
    start,
    end,
    height: WALL_HEIGHT,
    thickness: WALL_THICKNESS,
    material: wallMaterial,
    openings: [],
    baseY,
    floorId: id,
    roomId,
    visible: true,
    locked: false
  });

  walls.push(wall(0, [left, front], [centerX - 1.05, front]));
  walls.push(wall(1, [centerX + 1.05, front], [right, front]));
  walls.push(wall(2, [right, front], [right, back]));
  walls.push(wall(3, [right, back], [left, back]));
  walls.push(wall(4, [left, back], [left, front]));
  if (width > 9) {
    walls.push(wall(5, [left, centerZ - 0.8], [centerX - 0.75, centerZ - 0.8]));
    walls.push(wall(6, [centerX + 0.75, centerZ - 0.8], [right, centerZ - 0.8]));
  }

  const room = {
    id: roomId,
    name: level === 0 ? 'Main Space' : `Upper Space ${level}`,
    type: 'multipurpose',
    polygon: [[left, back], [right, back], [right, front], [left, front]],
    floorMaterial: template.generatorType === 'commercial' ? 'floor-concrete' : template.generatorType === 'service' ? 'floor-ceramic' : 'floor-oak',
    wallMaterial,
    ceilingMaterial: 'wall-warm',
    area: width * depth,
    objects: [],
    floorId: id,
    visible: true
  };

  const doors = [{
    id: objectId(template.id, level, 'door', 0),
    name: level === 0 ? 'Main Door' : 'Upper Door',
    type: 'door',
    position: [centerX, baseY, front + 0.02],
    rotation: [0, 0, 0],
    scale: [1, 1, 1],
    width: 1.7,
    height: 2.35,
    material: template.houseProfile?.accent ?? 'floor-oak',
    frameMaterial: template.houseProfile?.id?.startsWith('heritage') ? template.houseProfile.accent : 'metal-dark',
    modern: Boolean(template.houseProfile && !template.houseProfile.id.startsWith('heritage')),
    swingDirection: 'inward',
    floorId: id,
    roomId,
    visible: true,
    locked: false
  }];

  const glazing = template.houseProfile?.glazing ?? 'balanced';
  const windowWidth = glazing === 'wide' ? Math.min(2.35, width * 0.24) : glazing === 'vertical' ? 1.05 : 1.45;
  const windowHeight = glazing === 'vertical' ? 1.6 : glazing === 'wide' ? 1.42 : 1.25;
  const glassMaterial = template.houseProfile && glazing !== 'classic' ? 'glass-smoke' : 'glass-clear';
  const windowPositions = [
    [centerX - width * 0.28, baseY + 1.55, front + 0.09, 0],
    [centerX + width * 0.28, baseY + 1.55, front + 0.09, 0],
    [centerX - width * 0.25, baseY + 1.55, back - 0.09, Math.PI],
    [centerX + width * 0.25, baseY + 1.55, back - 0.09, Math.PI],
    [right + 0.09, baseY + 1.55, centerZ, -Math.PI / 2],
    [left - 0.09, baseY + 1.55, centerZ, Math.PI / 2]
  ];
  const windows = windowPositions.map(([x, y, z, ry], index) => ({
    id: objectId(template.id, level, 'window', index),
    name: `Window ${index + 1}`,
    type: 'window',
    position: [x, y, z],
    rotation: [0, ry, 0],
    scale: [1, 1, 1],
    width: windowWidth + ((template.previewSeed + index) % 2) * 0.12,
    height: windowHeight,
    sillHeight: 0.9,
    material: glassMaterial,
    frameMaterial: template.houseProfile?.glazing === 'classic' ? template.houseProfile.accent : 'metal-dark',
    deepFrame: Boolean(template.houseProfile?.deepFrames),
    floorId: id,
    roomId,
    visible: true,
    locked: false
  }));

  const floor = {
    id,
    name: level === 0 ? 'Ground Floor' : `Floor ${level + 1}`,
    level,
    baseY,
    height: FLOOR_HEIGHT,
    slabThickness: 0.22,
    rooms: [room],
    walls,
    doors,
    windows,
    stairs: [],
    measurements: [],
    objects: [],
    visible: true,
    locked: false
  };

  if (level < template.floors - 1) {
    floor.stairs.push({
      id: objectId(template.id, level, 'stair', 0),
      name: 'Floor Stair',
      type: 'stair',
      position: [centerX + width * 0.31, baseY + 0.02, centerZ],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      width: 1.1,
      rise: FLOOR_HEIGHT,
      run: 3.6,
      steps: 14,
      material: 'floor-oak',
      floorId: id,
      roomId,
      visible: true,
      locked: false
    });
  }

  floor.objects = addFurniturePack(template, floor, width, depth, centerX, centerZ);
  if (level > 0) floor.objects = floor.objects.slice(0, Math.max(3, Math.floor(floor.objects.length * 0.45)));
  return floor;
}

function makeSiteObjects(template) {
  const { width, depth } = template.footprint;
  const profile = template.houseProfile;
  const sitePadding = profile ? 7.5 : 8;
  const items = [{
    id: objectId(template.id, -1, 'ground', 0),
    name: 'Site Ground',
    type: 'ground',
    generatorType: 'ground',
    position: [0, -0.08, 0],
    rotation: [0, 0, 0],
    scale: [1, 1, 1],
    dimensions: [width + sitePadding, 0.16, depth + sitePadding],
    material: profile?.siteSurface === 'paved' || template.generatorType === 'commercial' ? 'paving' : 'grass',
    floorId: null,
    visible: true,
    locked: true
  }, {
    id: objectId(template.id, -1, 'path', 0),
    name: 'Entrance Paving',
    type: 'ground',
    generatorType: 'ground',
    position: [0, 0.015, depth / 2 + 2],
    rotation: [0, 0, 0],
    scale: [1, 1, 1],
    dimensions: [3, 0.08, 4.2],
    material: 'paving',
    floorId: null,
    visible: true,
    locked: true
  }];

  if (profile) {
    const density = Math.min(5, profile.landscapeDensity ?? 2);
    for (let index = 0; index < density; index += 1) {
      const side = index % 2 ? 1 : -1;
      items.push({
        id: objectId(template.id, -1, 'landscape', index),
        name: index === 0 && density >= 4 ? 'Feature Tree' : `Garden Shrub ${index + 1}`,
        type: 'landscape',
        generatorType: index === 0 && density >= 4 ? 'tree' : 'shrub',
        position: [side * (width / 2 + 1.05 + (index % 3) * 0.45), 0, depth / 2 - 0.45 - index * 0.72],
        rotation: [0, (template.previewSeed + index) * 0.17, 0],
        scale: [0.78 + (index % 2) * 0.22, 0.78 + (index % 3) * 0.12, 0.78 + (index % 2) * 0.22],
        dimensions: index === 0 && density >= 4 ? [1.25, 3.25, 1.25] : [0.82, 0.92, 0.82],
        material: 'grass',
        floorId: null,
        visible: true,
        locked: false
      });
    }
  }

  if (profile?.pool || template.generatorType === 'villa' || (template.generatorType === 'hospitality' && template.previewSeed % 2 === 0)) {
    const poolSide = profile?.mirror ? -1 : 1;
    items.push({
      id: objectId(template.id, -1, 'pool', 0),
      name: 'Outdoor Pool',
      type: 'pool',
      generatorType: 'pool',
      position: [poolSide * width * 0.2, 0, -depth / 2 - 1.7],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      dimensions: [Math.min(5.5, width * 0.48), 0.12, 2.5],
      material: 'water',
      floorId: null,
      visible: true,
      locked: false
    });
  }
  return items;
}

function makeRoof(template) {
  const topLevel = template.floors - 1;
  const massing = template.houseProfile
    ? getHouseFloorMassing(template, topLevel)
    : {
        width: template.footprint.width - topLevel * 0.35,
        depth: template.footprint.depth - topLevel * 0.25,
        centerX: 0,
        centerZ: 0,
        baseY: topLevel * FLOOR_HEIGHT
      };
  const { width, depth, centerX, centerZ, baseY } = massing;
  const flatTypes = new Set(['cafe', 'laundry', 'barbershop', 'retail', 'office', 'studio', 'service', 'commercial', 'education']);
  const roofStyle = template.houseProfile?.roofStyle
    ?? (flatTypes.has(template.generatorType) ? 'flat' : template.generatorType === 'villa' || template.previewSeed % 3 === 0 ? 'hip' : 'gable');
  return {
    id: objectId(template.id, topLevel, 'roof', 0),
    name: `${roofStyle} roof`,
    type: 'roof',
    roofStyle,
    dimensions: [width, 0.8, depth],
    position: [centerX, baseY + WALL_HEIGHT - 0.08, centerZ],
    rotation: [0, 0, 0],
    scale: [1, 1, 1],
    material: template.houseProfile?.roof ?? template.defaultMaterials[2] ?? 'roof-slate',
    parapetMaterial: template.houseProfile?.upperWall ?? 'concrete-light',
    roofHeight: template.houseProfile?.roofHeight,
    eave: template.houseProfile?.eave,
    floorId: objectId(template.id, topLevel, 'floor-record', 0),
    visible: true,
    locked: false
  };
}

export class TemplateGenerator {
  createProjectData(template) {
    if (!template?.id || !template?.footprint || template.floors < 1) throw new Error('Template data invalid.');
    const timestamp = new Date().toISOString();
    const floors = Array.from({ length: template.floors }, (_, level) => makeFloor(template, level));
    for (const item of createHouseArchitecture(template, floors)) {
      const floor = floors.find((candidate) => candidate.id === item.floorId) ?? floors[0];
      floor.objects.push(item);
    }
    floors[floors.length - 1].objects.push(makeRoof(template));
    const baseElevation = template.houseProfile?.baseElevation ?? 0;
    return {
      id: `project:${template.id}`,
      name: template.name,
      categoryId: template.categoryId,
      templateId: template.id,
      createdAt: timestamp,
      updatedAt: timestamp,
      units: 'metric',
      language: globalThis.document?.documentElement?.lang ?? 'en',
      quality: 'auto',
      floors,
      objects: makeSiteObjects(template),
      materials: clone(MATERIALS),
      cameraState: {
        mode: 'orbit',
        position: [template.footprint.width * 1.2, template.floors * 4.2, template.footprint.depth * 1.45],
        target: [0, template.floors * 1.35, 0]
      },
      cameraStart: [0, baseElevation + 1.65, template.footprint.depth / 2 + 2.3],
      historyMeta: { entries: 0 },
      metadata: {
        style: template.style,
        generatorType: template.generatorType,
        previewSeed: template.previewSeed,
        footprint: clone(template.footprint),
        description: template.description,
        ...(template.houseProfile ? { houseProfile: clone(template.houseProfile) } : {})
      },
      appVersion: '0.1.0',
      schemaVersion: 1
    };
  }

  generate(template, objectFactory) {
    const projectData = this.createProjectData(template);
    const { threeGroup, renderStats } = this.groupFromProject(projectData, objectFactory);
    return { projectData, threeGroup, renderStats };
  }

  groupFromProject(projectData, objectFactory) {
    if (!objectFactory) throw new Error('ObjectFactory is required for mesh generation.');
    const root = new THREE.Group();
    root.name = 'KIPS_TEMPLATE_ROOT';
    root.userData.templateId = projectData.templateId;
    root.userData.categoryId = projectData.categoryId;

    for (const item of projectData.objects ?? []) {
      if (item.visible === false) continue;
      const object = ['ground', 'pool', 'landscape'].includes(item.type)
        ? objectFactory.createLandscapeMesh(item)
        : objectFactory.createFurnitureMesh(item.catalogId, item);
      root.add(object);
    }

    for (const floor of projectData.floors ?? []) {
      if (floor.visible === false) continue;
      for (const room of floor.rooms ?? []) if (room.visible !== false) root.add(objectFactory.createFloorMesh(room, floor));
      for (const wall of floor.walls ?? []) if (wall.visible !== false) root.add(objectFactory.createWallMesh(wall));
      for (const door of floor.doors ?? []) if (door.visible !== false) root.add(objectFactory.createDoorMesh(door));
      for (const windowData of floor.windows ?? []) if (windowData.visible !== false) root.add(objectFactory.createWindowMesh(windowData));
      for (const stair of floor.stairs ?? []) if (stair.visible !== false) root.add(objectFactory.createStairMesh(stair));
      for (const item of floor.objects ?? []) {
        if (item.visible === false) continue;
        if (item.type === 'roof') root.add(objectFactory.createRoofMesh(item));
        else if (item.type === 'architecture') root.add(objectFactory.createArchitecturalMesh(item));
        else root.add(objectFactory.createFurnitureMesh(item.catalogId, item));
      }
    }

    const renderStats = validateTemplateRendered(root, projectData);
    return { threeGroup: root, renderStats };
  }
}

export function validateTemplateRendered(root, projectData) {
  if (!root || root.name !== 'KIPS_TEMPLATE_ROOT') throw new Error('Template root missing.');
  let meshCount = 0;
  let visibleMeshCount = 0;
  const materials = new Set();
  const objectIds = new Set();
  root.updateMatrixWorld(true);
  root.traverse((child) => {
    if (child.isMesh) {
      meshCount += 1;
      if (child.visible) visibleMeshCount += 1;
      if (Array.isArray(child.material)) child.material.forEach((item) => materials.add(item.uuid));
      else if (child.material) materials.add(child.material.uuid);
      if (child.userData.objectId) objectIds.add(child.userData.objectId);
    }
  });
  if (meshCount <= 0 || visibleMeshCount <= 0) throw new Error('Template mesh generation failed.');
  const box = new THREE.Box3().setFromObject(root);
  const size = new THREE.Vector3();
  box.getSize(size);
  if (box.isEmpty() || Math.max(size.x, size.y, size.z) < 1) throw new Error('Bounding box empty.');
  const floors = projectData.floors ?? [];
  const wallCount = floors.reduce((sum, floor) => sum + (floor.walls?.length ?? 0), 0);
  if (!floors.length || wallCount < 4) throw new Error('Template requires at least one floor and four walls.');
  return {
    templateId: projectData.templateId,
    categoryId: projectData.categoryId,
    objectCount: objectIds.size,
    meshCount,
    visibleMeshCount,
    materialCount: materials.size,
    floorCount: floors.length,
    wallCount,
    doorCount: floors.reduce((sum, floor) => sum + (floor.doors?.length ?? 0), 0),
    windowCount: floors.reduce((sum, floor) => sum + (floor.windows?.length ?? 0), 0),
    furnitureCount: floors.reduce((sum, floor) => sum + (floor.objects?.filter((item) => item.type === 'furniture').length ?? 0), 0),
    architectureCount: floors.reduce((sum, floor) => sum + (floor.objects?.filter((item) => item.type === 'architecture').length ?? 0), 0),
    boundingBox: {
      min: box.min.toArray(),
      max: box.max.toArray(),
      size: size.toArray()
    },
    status: 'rendered'
  };
}

export const templateGenerator = new TemplateGenerator();
